import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft, Check, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };
const stagger = { show: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } } };

export const Login = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (user) navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
  }, [user, navigate, location.state?.from?.pathname]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(loginForm.email, loginForm.password);
    if (result.success) navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) { toast.error("Please enter your email address"); return; }
    setSendingReset(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setResetSent(true);
      toast.success("Password reset link sent to your email!");
    } catch { toast.error("Failed to send reset link. Please try again."); }
    finally { setSendingReset(false); }
  };

  const closeForgotModal = () => { setShowForgotModal(false); setForgotEmail(""); setResetSent(false); };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "#050505" }}>
      {/* Left Panel — Cosmic Visual */}
      <div className="hidden lg:block relative overflow-hidden">
        {/* Background Image with slow zoom animation */}
        <motion.div
          className="absolute inset-0"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        >
          <img
            src="https://images.pexels.com/photos/30052971/pexels-photo-30052971.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
            alt="Cosmic"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </motion.div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

        {/* Content */}
        <motion.div
          className="relative z-10 flex flex-col justify-between h-full p-12 xl:p-16"
          initial="hidden" animate="show" variants={stagger}
        >
          {/* Logo */}
          <motion.div variants={fadeUp} className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#E8913A] flex items-center justify-center shadow-lg shadow-[#E8913A]/30">
              <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
              </svg>
            </div>
            <div>
              <span className="text-xl font-bold text-white tracking-tight">Zulton</span>
              <span className="block text-[10px] font-semibold tracking-[0.35em] text-[#E8913A] uppercase">Universe</span>
            </div>
          </motion.div>

          {/* Main Typography */}
          <div className="max-w-lg">
            <motion.h1 variants={fadeUp} className="text-5xl xl:text-6xl font-bold leading-[1.1] tracking-tight">
              <span className="text-white">Explore the</span><br />
              <span className="text-[#E8913A]">Universe</span>
              <span className="text-white"> of</span><br />
              <span className="text-white">Talent</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 text-lg text-neutral-400 leading-relaxed max-w-md">
              Navigate your workforce through the stars. Manage talent, track performance orbits, and build constellations of success.
            </motion.p>
          </div>

          {/* Bottom Stats */}
          <motion.div variants={fadeUp} className="flex gap-8">
            {[
              { label: "Modules", value: "25+" },
              { label: "Automation", value: "Real-time" },
              { label: "Enterprise", value: "Grade" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="relative flex items-center justify-center p-6 sm:p-12 lg:p-16 xl:p-24">
        {/* Atmospheric glow behind card */}
        <div className="absolute w-[400px] h-[400px] bg-[#E8913A]/8 rounded-full blur-[120px] pointer-events-none" />

        <motion.div
          className="relative z-10 w-full max-w-md"
          initial="hidden" animate="show" variants={stagger}
        >
          {/* Mobile Logo */}
          <motion.div variants={fadeUp} className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#E8913A] flex items-center justify-center">
              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
              </svg>
            </div>
            <div>
              <span className="text-lg font-bold text-white tracking-tight">Zulton</span>
              <span className="block text-[9px] font-semibold tracking-[0.3em] text-[#E8913A] uppercase">Universe</span>
            </div>
          </motion.div>

          {/* Glassmorphic Card */}
          <motion.div
            variants={fadeUp}
            className="rounded-3xl p-8 sm:p-10 border"
            style={{
              background: "rgba(255,255,255,0.03)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              borderColor: "rgba(255,255,255,0.08)",
              boxShadow: "0 32px 64px rgba(0,0,0,0.5)",
            }}
          >
            <motion.div variants={fadeUp}>
              <h2 className="text-2xl font-bold text-white tracking-tight">Welcome back</h2>
              <p className="text-neutral-500 text-sm mt-1">Sign in to access your universe</p>
            </motion.div>

            <form onSubmit={handleLogin} className="mt-8 space-y-5" data-testid="login-form">
              <motion.div variants={fadeUp} className="space-y-2">
                <Label className="text-neutral-300 text-sm font-medium tracking-wide">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-600" />
                  <input
                    type="email"
                    placeholder="you@company.com"
                    className="w-full h-12 pl-11 pr-4 rounded-xl text-white placeholder-neutral-600 transition-all duration-300 outline-none"
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                    onFocus={e => { e.target.style.borderColor = "#E8913A"; e.target.style.boxShadow = "0 0 0 2px rgba(232,145,58,0.15)"; }}
                    onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.boxShadow = "none"; }}
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-neutral-300 text-sm font-medium tracking-wide">Password</Label>
                  <button type="button" onClick={() => setShowForgotModal(true)}
                    className="text-xs text-[#E8913A] hover:text-[#F0A65B] transition-colors hover:underline underline-offset-4"
                    data-testid="forgot-password-link">
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-600" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="w-full h-12 pl-11 pr-12 rounded-xl text-white placeholder-neutral-600 transition-all duration-300 outline-none"
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                    onFocus={e => { e.target.style.borderColor = "#E8913A"; e.target.style.boxShadow = "0 0 0 2px rgba(232,145,58,0.15)"; }}
                    onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.boxShadow = "none"; }}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300 transition-colors">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </motion.div>

              <motion.div variants={fadeUp}>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                  style={{
                    background: "#E8913A",
                    color: "#000",
                    boxShadow: "0 0 20px rgba(232,145,58,0.25)",
                  }}
                  onMouseEnter={e => { e.target.style.transform = "scale(1.02)"; e.target.style.boxShadow = "0 0 32px rgba(232,145,58,0.45)"; }}
                  onMouseLeave={e => { e.target.style.transform = "scale(1)"; e.target.style.boxShadow = "0 0 20px rgba(232,145,58,0.25)"; }}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</>
                  ) : (
                    <>Sign In <ArrowRight className="h-4 w-4" /></>
                  )}
                </button>
              </motion.div>
            </form>

            <motion.p variants={fadeUp} className="mt-8 text-center text-xs text-neutral-600">
              Employees join via admin invitation only
            </motion.p>
          </motion.div>

          {/* Bottom branding */}
          <motion.div variants={fadeUp} className="mt-8 text-center">
            <p className="text-[11px] text-neutral-700">
              Powered by <span className="text-neutral-500 font-medium">Zulton Ventures Pvt. Ltd.</span>
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Forgot Password Modal — Dark Glassmorphic */}
      <Dialog open={showForgotModal} onOpenChange={closeForgotModal}>
        <DialogContent className="max-w-md border-0 rounded-2xl" style={{
          background: "rgba(10,10,10,0.95)",
          backdropFilter: "blur(24px)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Lock className="w-5 h-5 text-[#E8913A]" />
              {resetSent ? "Check Your Email" : "Forgot Password"}
            </DialogTitle>
            <DialogDescription className="text-neutral-500">
              {resetSent ? "A reset link has been sent to your inbox." : "Reset your password via email."}
            </DialogDescription>
          </DialogHeader>

          {!resetSent ? (
            <>
              <div className="py-4">
                <p className="text-sm text-neutral-400 mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-600" />
                    <input
                      type="email"
                      className="w-full h-12 pl-11 pr-4 rounded-xl text-white placeholder-neutral-600 outline-none"
                      style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.1)" }}
                      placeholder="you@company.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeForgotModal} disabled={sendingReset}
                  className="border-neutral-700 text-neutral-300 hover:bg-neutral-800">Cancel</Button>
                <button onClick={handleForgotPassword} disabled={sendingReset}
                  className="px-5 h-10 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all"
                  style={{ background: "#E8913A", color: "#000" }}
                  data-testid="send-reset-btn">
                  {sendingReset ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4" /> Send Reset Link</>}
                </button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-6 text-center">
              <div className="w-16 h-16 mx-auto bg-green-900/30 border border-green-800/50 rounded-full flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Email Sent!</h3>
              <p className="text-neutral-400 text-sm mb-4">
                We've sent a password reset link to <strong className="text-white">{forgotEmail}</strong>
              </p>
              <p className="text-neutral-600 text-xs mb-6">
                Check your inbox and click the link to reset your password. The link expires in 1 hour.
              </p>
              <button onClick={closeForgotModal}
                className="px-5 h-10 rounded-xl text-sm font-semibold flex items-center gap-2 mx-auto transition-all"
                style={{ background: "#E8913A", color: "#000" }}>
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
